import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { signalsData } from "Stores/signalsData";
import AgentSelect from "./AgentSelect";
import { defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, SequenceField, NotifyStep, openInBuilder as openStepsInBuilder } from "./SetupFlow";
import { SignalTriggerPicker } from "./StartsFromPicker";
import { savedOrAlwaysOn, typeOfAlwaysOnValue } from "../signals/signalMeta";
import SellerProfileLine from "../signals/SellerProfileLine";

/** The value a field starts with: its default, or empty. A list field takes `listId` (the list the feed habit used) when given. */
export const initialValues = (template, listId = "") => {
  const out = {};
  ((template && template.fields) || []).forEach(f => {
    out[f.key] = f.type === "contact_list" && listId ? listId : f.default !== undefined ? f.default : "";
  });
  return out;
};

/**
 * Hire an agent that starts when one of your saved signals fires. The signal's list and conditions stay
 * managed in Signals — this page only picks which signal to listen to and what to do about it: a ready-made
 * template (each asks for just the few things it needs), or "Build your own" in the canvas builder.
 * Arriving from Signals (?signalId=…&from=signals) locks the signal and offers a way back; &template=<key> preselects a template
 * and &listId=<id> prefills its list field. ?type=<signalType> (a template card) preselects that signal, yours if you track it.
 */
const CreateSignalAgent = observer(() => {
  const history = useHistory();
  const params = new URLSearchParams(useLocation().search);
  const fromSignals = params.get("from") === "signals";
  const templateParam = params.get("template");
  const listParam = params.get("listId") || "";
  const [signalId, setSignalId] = useState(params.get("signalId") || "");
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [sequenceName, setSequenceName] = useState("");
  const [name, setName] = useState("");
  const [approval, setApproval] = useState("REQUIRED");
  const [notifyMode, setNotifyMode] = useState("ON_FAILURE");
  const [saving, setSaving] = useState(false);
  // The seller profile the agent writes for: the signal's own, else the team's default (the line hands it back).
  const [sellerProfileId, setSellerProfileId] = useState(null);
  const timezone = defaultTimezone();

  useEffect(() => {
    signalsData.fetchSaved();
    if (!signalsData.catalog.length) signalsData.fetchCatalog(); // free signals' always-on ids live in the catalog
    agentsData.fetchSequencesLite();
    agentsData.fetchContactLists();
    agentsData.fetchSlackChannels();
  }, []);

  const saved = savedOrAlwaysOn(signalsData.saved, signalsData.catalog, signalId);

  useEffect(() => {
    setSellerProfileId((saved && saved.signal && saved.signal.sellerProfileId) || null);
  }, [signalId, saved && saved.signal.sellerProfileId]); // eslint-disable-line

  // Templates depend on the signal's type (a contact signal and an account signal act differently).
  useEffect(() => {
    setTemplate(null);
    setTemplates([]);
    if (saved) {
      agentsData.fetchSignalTemplates(saved.signal.signalType, saved.signal.id).then(list => {
        setTemplates(list);
        // ?template=<key> (from the Signals feed) preselects that template; unknown keys are ignored.
        const preset = templateParam && (list || []).find(t => t.key === templateParam);
        if (preset) choose(preset);
      });
    }
  }, [signalId, saved && saved.signal.signalType]); // eslint-disable-line

  const fields = (template && template.fields) || [];
  const sends = fields.some(f => f.type === "sequence");
  const missing = fields.some(f => f.required && !String(values[f.key] === undefined ? "" : values[f.key]).trim());
  const canSubmit = !!saved && !!template && !missing;
  // Came from a signal's Automate: the signal is fixed, so the page asks only what the agent should do.
  const locked = !!(fromSignals && signalId && saved);

  const choose = t => {
    setTemplate(t);
    setValues(initialValues(t, listParam));
    setSequenceName("");
    setName("");
  };

  const payloadFor = key => ({
    ...(typeOfAlwaysOnValue(signalId) ? { signalType: typeOfAlwaysOnValue(signalId) } : { savedSignalId: signalId }),
    template: key,
    name: name.trim() || (template ? `${template.name} · ${saved.displayName}` : `Signal agent · ${saved.displayName}`),
    ...(template && key === template.key ? values : {}),
    ...(sends && template && key === template.key ? { approvalMode: approval } : {}),
    notifyMode,
    timezone,
    ...(sellerProfileId ? { sellerProfileId } : {})
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createSignalAgent(payloadFor(template.key));
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Opens the steps in the canvas builder to change before creating: the chosen template, or an empty start.
  const openInBuilder = key => openStepsInBuilder(history, "signal-agent", payloadFor(key), setSaving);

  const back = () => history.push(fromSignals ? "/signals?tab=mine" : "/agents/new");
  const signalOptions = signalsData.saved.map(s => ({ value: s.signal.id, label: s.displayName }))
    .concat(saved && saved.alwaysOn ? [{ value: signalId, label: saved.displayName }] : []);
  const setValue = (key, v) => setValues({ ...values, [key]: v });

  const renderField = f => {
    const label = f.required ? f.label : `${f.label} (optional)`;
    if (f.type === "sequence") {
      return (
        <Field key={f.key} label={label}>
          <SequenceField id={values[f.key]} name={sequenceName} placeholder="Select a sequence"
            onPick={x => { setValue(f.key, x.id); setSequenceName(x.name); }} />
        </Field>
      );
    }
    if (f.type === "contact_list") {
      return (
        <Field key={f.key} label={label}>
          <AgentSelect value={values[f.key]} placeholder="Choose a list" onChange={v => setValue(f.key, v)}
            options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.name }))} />
        </Field>
      );
    }
    if (f.type === "slack_channel") {
      const channels = agentsData.slackChannels || [];
      return (
        <Field key={f.key} label={label} warn
          hint={agentsData.slackChannelsLoaded && channels.length === 0 ? "Slack isn't connected for your team yet, so nothing can be posted." : null}>
          <AgentSelect value={values[f.key]} onChange={v => setValue(f.key, v)}
            options={[{ value: "", label: "The channel my team connected" }, ...channels.map(c => ({ value: c.id, label: c.name }))]} />
        </Field>
      );
    }
    if (f.type === "textarea") {
      return (
        <Field key={f.key} label={label}>
          <textarea className="ag-input" rows={3} value={values[f.key]} onChange={e => setValue(f.key, e.target.value)} />
        </Field>
      );
    }
    return (
      <Field key={f.key} label={label}>
        <input className="ag-input" type={f.type === "number" ? "number" : "text"} min={f.type === "number" ? 0 : undefined}
          value={values[f.key]} placeholder={f.hint || ""} onChange={e => setValue(f.key, e.target.value)} />
      </Field>
    );
  };

  return (
    <SetupPage
      title={locked ? "Automate this signal" : "Hire · Signal agent"}
      back={<button className="ag-link" onClick={back}>{fromSignals ? "← Back to Signals" : "← Back"}</button>}
      name={template ? { value: name, onChange: setName, placeholder: `${template.name} · ${saved.displayName}` } : null}
      bar={{
        canSubmit, saving, onCreate: submit, onCancel: back,
        onCustomize: () => openInBuilder(template.key), customizeDisabled: !canSubmit,
        reason: !saved ? "Pick a signal to continue." : !template ? "Pick what it does to continue." : "Fill in the required fields to continue."
      }}>
      <When icon={saved ? saved.signal.signalType : "bolt"}>
        {locked ? (
          // Came from Signals with the signal picked: one line says what starts it; the page is about the job.
          <div className="ag-startsline">
            <b className="ag-sf__title">{saved.displayName} finds a move</b>
            <button className="ag-link" onClick={() => history.push("/signals?tab=mine")}>Edit the signal</button>
          </div>
        ) : fromSignals && signalId ? (
          <AgentSelect value={signalId} disabled placeholder="Choose a signal" onChange={setSignalId} options={signalOptions} />
        ) : (
          <SignalTriggerPicker value={signalId} onChange={setSignalId} suggestedType={params.get("type") || ""} />
        )}
        {saved && !locked && (
          <span className="ag-sf__hint">
            Watching {saved.scopeLabel}. <button className="ag-link" onClick={() => history.push("/signals?tab=mine")}>Edit the signal</button>
          </span>
        )}
        {saved && <SellerProfileLine className="sg-sp--gap" value={sellerProfileId} onChange={setSellerProfileId} />}
      </When>
      {saved && (
        <Step icon="robot" tone="violet" title={locked ? "Pick what it does" : "What should it do?"}>
          <div className="ag-templates">
            {templates.map(t => (
              <button key={t.key} type="button" aria-pressed={!!(template && template.key === t.key)} className={`ag-template ${template && template.key === t.key ? "on" : ""}`} onClick={() => choose(t)}>
                <span className="ag-template__radio" aria-hidden="true" />
                <b>{t.name}</b>
                <span>{t.description}</span>
                {!locked && <small>{t.steps.join(" → ")}</small>}
              </button>
            ))}
            <button type="button" className="ag-template ag-template--own" disabled={saving} onClick={() => openInBuilder("blank")}>
              <b>＋ Build your own</b>
              <span>Pick the steps yourself in the builder.</span>
            </button>
          </div>
        </Step>
      )}
      {template && (fields.length > 0 || sends) && (
        <Step icon={sends ? "send" : "edit"} tone={sends ? "amber" : "grey"} title="Set it up" tags={[sends && approval === "REQUIRED" && { label: "Asks you first", ask: true }]}>
          {fields.map(renderField)}
          {sends && (
            <Field label="Before anything is sent">
              <AgentSelect value={approval} onChange={setApproval} options={[
                { value: "REQUIRED", label: "Ask me first. Drafts wait in Approvals" },
                { value: "NONE", label: "Send automatically" }
              ]} />
            </Field>
          )}
        </Step>
      )}
      {template && <NotifyStep value={notifyMode} onChange={setNotifyMode} />}
    </SetupPage>
  );
});

export default CreateSignalAgent;
